import { useCallback, useEffect, useRef, useState } from 'react';

export type RecordingKind = 'audio' | 'video';

export type RecorderErrorReason =
  | 'unsupported'
  | 'permissionDenied'
  | 'deviceNotFound'
  | 'deviceInUse'
  | 'recordingFailed';

export type Recording = {
  file: File;
  url: string;
  durationMs: number;
};

export type RecorderState =
  | { status: 'idle' }
  | { status: 'requestingPermission' }
  | { status: 'ready'; stream: MediaStream }
  | { status: 'recording'; stream: MediaStream }
  | { status: 'paused'; stream: MediaStream }
  | { status: 'processing' }
  | { status: 'recorded'; recording: Recording }
  | { status: 'error'; reason: RecorderErrorReason };

// MP4 with H.264 and AAC plays and seeks everywhere once remuxed; Safari can't
// seek longer WebM recordings. Firefox only records WebM, and older Safari
// rejects the codec list, so both fall further down.
const MIME_TYPE_CANDIDATES: Record<RecordingKind, string[]> = {
  audio: [
    'audio/mp4;codecs=mp4a.40.2',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ],
  video: [
    'video/mp4;codecs=avc1,mp4a.40.2',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ],
};

const FILE_EXTENSIONS: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/mp4': 'm4a',
  'audio/ogg': 'ogg',
  'video/webm': 'webm',
  'video/mp4': 'mp4',
};

const MEDIA_CONSTRAINTS: Record<RecordingKind, MediaStreamConstraints> = {
  audio: {
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  },
  video: {
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    video: {
      facingMode: 'user',
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30, max: 30 },
    },
  },
};

// Speech stays clear at 64 kbps Opus. 1 Mbps keeps 720p video at roughly
// 7.5 MB a minute, which matters for students uploading over mobile data.
const AUDIO_BITS_PER_SECOND = 64_000;
const VIDEO_BITS_PER_SECOND = 1_000_000;

const CHUNK_INTERVAL_MS = 1000;
const ELAPSED_TICK_MS = 250;

export function isMediaRecordingSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

function pickMimeType(kind: RecordingKind): string | undefined {
  return MIME_TYPE_CANDIDATES[kind].find((mimeType) => MediaRecorder.isTypeSupported(mimeType));
}

function baseMimeType(mimeType: string): string {
  return mimeType.split(';')[0]?.trim() ?? mimeType;
}

function errorReasonFor(error: unknown): RecorderErrorReason {
  if (!(error instanceof DOMException)) {
    return 'recordingFailed';
  }
  switch (error.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'permissionDenied';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'deviceNotFound';
    case 'NotReadableError':
    case 'AbortError':
      return 'deviceInUse';
    default:
      return 'recordingFailed';
  }
}

function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

export function recordingFilename(kind: RecordingKind, mimeType: string, date: Date): string {
  const label = kind === 'audio' ? 'Audio recording' : 'Video recording';
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const time = `${pad(date.getHours())}.${pad(date.getMinutes())}.${pad(date.getSeconds())}`;
  const extension = FILE_EXTENSIONS[mimeType] ?? 'webm';
  return `${label} ${day} ${time}.${extension}`;
}

function stopTracks(stream: MediaStream | null) {
  stream?.getTracks().forEach((track) => track.stop());
}

export function useMediaRecorder({
  kind,
  maxDurationMs,
}: {
  kind: RecordingKind;
  maxDurationMs: number;
}) {
  const [state, setState] = useState<RecorderState>({ status: 'idle' });
  const [elapsedMs, setElapsedMs] = useState(0);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingUrlRef = useRef<string | null>(null);
  const accumulatedMsRef = useRef(0);
  const segmentStartedAtRef = useRef<number | null>(null);
  // Bumped whenever the recorder is torn down, so a permission prompt or a
  // stop that resolves afterwards can tell its result is no longer wanted.
  const generationRef = useRef(0);

  const currentElapsedMs = useCallback(() => {
    const segmentStartedAt = segmentStartedAtRef.current;
    return (
      accumulatedMsRef.current +
      (segmentStartedAt === null ? 0 : performance.now() - segmentStartedAt)
    );
  }, []);

  const releaseRecording = useCallback(() => {
    if (recordingUrlRef.current !== null) {
      URL.revokeObjectURL(recordingUrlRef.current);
      recordingUrlRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    generationRef.current += 1;
    const recorder = recorderRef.current;
    if (recorder !== null) {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
      recorderRef.current = null;
    }
    stopTracks(streamRef.current);
    streamRef.current = null;
    chunksRef.current = [];
    accumulatedMsRef.current = 0;
    segmentStartedAtRef.current = null;
    releaseRecording();
  }, [releaseRecording]);

  const prepare = useCallback(async () => {
    teardown();
    setElapsedMs(0);

    if (!isMediaRecordingSupported() || pickMimeType(kind) === undefined) {
      setState({ status: 'error', reason: 'unsupported' });
      return;
    }

    const generation = generationRef.current;
    setState({ status: 'requestingPermission' });

    try {
      const stream = await navigator.mediaDevices.getUserMedia(MEDIA_CONSTRAINTS[kind]);
      if (generation !== generationRef.current) {
        stopTracks(stream);
        return;
      }
      // A device unplugged or a permission revoked ends the track. Keep what
      // was captured if recording, otherwise say the device has gone.
      stream.getTracks().forEach((track) => {
        track.onended = () => {
          const recorder = recorderRef.current;
          if (recorder !== null && recorder.state !== 'inactive') {
            recorder.stop();
            setState({ status: 'processing' });
          } else if (recorder === null && streamRef.current === stream) {
            teardown();
            setState({ status: 'error', reason: 'deviceNotFound' });
          }
        };
      });
      streamRef.current = stream;
      setState({ status: 'ready', stream });
    } catch (error) {
      if (generation === generationRef.current) {
        setState({ status: 'error', reason: errorReasonFor(error) });
      }
    }
  }, [kind, teardown]);

  const finalise = useCallback(
    async (recorder: MediaRecorder, generation: number, requestedMimeType: string) => {
      const durationMs = currentElapsedMs();
      segmentStartedAtRef.current = null;
      accumulatedMsRef.current = durationMs;

      stopTracks(streamRef.current);
      streamRef.current = null;
      recorderRef.current = null;

      const mimeType = baseMimeType(recorder.mimeType || requestedMimeType);
      const rawBlob = new Blob(chunksRef.current, { type: mimeType });
      chunksRef.current = [];

      // Loaded only once a recording finishes, so pages that never record skip it.
      const blob = await import('./remuxRecording').then(
        ({ remuxRecording }) => remuxRecording(rawBlob),
        () => rawBlob,
      );

      if (generation !== generationRef.current) {
        return;
      }

      if (blob.size === 0) {
        setState({ status: 'error', reason: 'recordingFailed' });
        return;
      }

      const file = new File([blob], recordingFilename(kind, mimeType, new Date()), {
        type: mimeType,
      });
      const url = URL.createObjectURL(file);
      recordingUrlRef.current = url;
      setState({ status: 'recorded', recording: { file, url, durationMs } });
    },
    [currentElapsedMs, kind],
  );

  const start = useCallback(() => {
    const stream = streamRef.current;
    const mimeType = pickMimeType(kind);
    if (stream === null || mimeType === undefined) {
      return;
    }

    const generation = generationRef.current;

    try {
      const recorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
        ...(kind === 'video' ? { videoBitsPerSecond: VIDEO_BITS_PER_SECOND } : {}),
      });

      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        void finalise(recorder, generation, mimeType);
      };
      recorder.onerror = () => {
        teardown();
        setState({ status: 'error', reason: 'recordingFailed' });
      };

      recorderRef.current = recorder;
      accumulatedMsRef.current = 0;
      segmentStartedAtRef.current = performance.now();
      setElapsedMs(0);
      recorder.start(CHUNK_INTERVAL_MS);
      setState({ status: 'recording', stream });
    } catch {
      teardown();
      setState({ status: 'error', reason: 'recordingFailed' });
    }
  }, [finalise, kind, teardown]);

  const pause = useCallback(() => {
    const recorder = recorderRef.current;
    const stream = streamRef.current;
    if (recorder?.state !== 'recording' || stream === null) {
      return;
    }
    recorder.pause();
    accumulatedMsRef.current = currentElapsedMs();
    segmentStartedAtRef.current = null;
    setElapsedMs(accumulatedMsRef.current);
    setState({ status: 'paused', stream });
  }, [currentElapsedMs]);

  const resume = useCallback(() => {
    const recorder = recorderRef.current;
    const stream = streamRef.current;
    if (recorder?.state !== 'paused' || stream === null) {
      return;
    }
    recorder.resume();
    segmentStartedAtRef.current = performance.now();
    setState({ status: 'recording', stream });
  }, []);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder === null || recorder.state === 'inactive') {
      return;
    }
    recorder.stop();
    setState({ status: 'processing' });
  }, []);

  const discard = useCallback(() => {
    teardown();
    setElapsedMs(0);
    setState({ status: 'idle' });
  }, [teardown]);

  useEffect(() => {
    if (state.status !== 'recording') {
      return;
    }
    const interval = window.setInterval(() => {
      const elapsed = currentElapsedMs();
      setElapsedMs(elapsed);
      if (elapsed >= maxDurationMs) {
        stop();
      }
    }, ELAPSED_TICK_MS);
    return () => window.clearInterval(interval);
  }, [currentElapsedMs, maxDurationMs, state.status, stop]);

  const hasUnsavedRecording =
    state.status === 'recording' ||
    state.status === 'paused' ||
    state.status === 'processing' ||
    state.status === 'recorded';

  useEffect(() => {
    if (!hasUnsavedRecording) {
      return;
    }
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasUnsavedRecording]);

  useEffect(() => teardown, [teardown]);

  return {
    state,
    elapsedMs,
    hasUnsavedRecording,
    prepare,
    start,
    pause,
    resume,
    stop,
    discard,
  };
}
